// Country-level border outlines, derived from region polygons by cancelling
// edges shared between two regions of the same country. Adjacent regions in
// this dataset share exact boundary coordinates (verified against the real
// GeoJSON: a 51-region country has ~34% of its edges bit-identical with a
// neighbour), so plain coordinate matching is enough — no polygon union
// library needed, and it's immune to the self-intersection/precision errors
// that make libraries like turf/polyclip fail on messy real-world geometry.
const ROUND = 6 // ~0.1m at this scale — collapses float noise, not real vertices

const keyOf = (pt) => `${pt[0].toFixed(ROUND)},${pt[1].toFixed(ROUND)}`

const collectRingEdges = (ring, out) => {
  for (let i = 0; i < ring.length - 1; i++) {
    const a = ring[i]
    const b = ring[i + 1]
    const ka = keyOf(a)
    const kb = keyOf(b)
    out.push({ edgeKey: ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`, a, b })
  }
}

const collectGeometryEdges = (geometry, out) => {
  if (!geometry) return
  if (geometry.type === 'Polygon') {
    geometry.coordinates.forEach((ring) => collectRingEdges(ring, out))
  } else if (geometry.type === 'MultiPolygon') {
    geometry.coordinates.forEach((poly) => poly.forEach((ring) => collectRingEdges(ring, out)))
  }
}

/** Build a FeatureCollection of LineStrings tracing each country's outline. */
export const buildCountryBorderLines = (features) => {
  const byCountry = new Map()
  features.forEach((f) => {
    const cid = f.properties?.country_id
    if (cid == null) return
    if (!byCountry.has(cid)) byCountry.set(cid, [])
    byCountry.get(cid).push(f)
  })

  const lineFeatures = []
  byCountry.forEach((regionFeatures, cid) => {
    const edges = []
    regionFeatures.forEach((f) => collectGeometryEdges(f.geometry, edges))

    const counts = new Map()
    edges.forEach(({ edgeKey }) => counts.set(edgeKey, (counts.get(edgeKey) || 0) + 1))

    edges.forEach(({ edgeKey, a, b }) => {
      if (counts.get(edgeKey) === 1) {
        lineFeatures.push({
          type: 'Feature',
          properties: { country_id: cid },
          geometry: { type: 'LineString', coordinates: [a, b] },
        })
      }
    })
  })

  return { type: 'FeatureCollection', features: lineFeatures }
}
